import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./StaticPaywall.module.scss"

/**
 * StaticPaywall (`/static`) — an original, self-contained ONBOARDING paywall for
 * Private Photo Vault Premium. Paywall type rolled at random for this run:
 * ONBOARDING.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it: the pitch is the one
 * thing security & privacy is really about — ENCRYPTION. Without your key, a
 * private photo is not a blurred photo or a locked door. It is nothing you can
 * read. It is static.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "To everyone else, static. To you, everything." A new photo you save lands in
 * the vault as encrypted noise — literal, animated cipher-static, unreadable to
 * anyone. Then YOUR key sweeps across it and the static resolves, grain by
 * grain, back into the memory it always was: a warm beach, a birthday, a dog on
 * a hill. Drag the key back and it dissolves to static again — because the photo
 * is only ever a memory under your key. The onboarding welcome is that contrast,
 * made physical: the same wall of tiles is meaningless to the world and precious
 * to you, and the only thing standing between the two is the key that is yours
 * alone.
 *
 * ── Divergence (vs. the 90+ prior concepts, incl. the security cluster) ─────
 * Deliberately far from its security neighbours (Ward crest, Hallmark seal,
 * OnlyYou keypad, Vault Door, Keyholder, Perimeter geofence, Cipher, PlainSight,
 * Decoy, Deep Freeze, Standby console):
 * Layout      : a full-bleed ENCRYPTION WALL — a mosaic of memory tiles living
 *               as animated cipher-static, over an ink canvas. Not a card list,
 *               a crest, a certificate, a keypad, a door or a console.
 * Interaction : SLIDE-YOUR-KEY — a key pill you drag along a track; the wall
 *               decrypts in proportion to how far the key has travelled, and
 *               re-scrambles if you slide it back. New to the set: not a press-
 *               and-hold converge (Ward), not a tilt-seal (Hallmark), not a
 *               keypad (OnlyYou), not a dial (Tumbler), not a power switch.
 * Metaphor    : ENCRYPTION itself — ciphertext vs. plaintext, noise vs. memory.
 *               The rest state is genuine static, not a blur or a shadow.
 * Motion      : DECRYPT / RESOLVE — SVG turbulence displacement melting to zero
 *               grain by grain under a travelling key, a deliberate turn away
 *               from "pieces converge" or "light spreads across a row".
 * Feature     : the real protections are the KEY'S TEETH — each tooth on the key
 *               names a real safeguard, and lights as the key seats home; not a
 *               separate bullet list.
 * Value frame : CONTRAST framing — the identical wall is worthless to everyone
 *               else and everything to you; the key is the whole difference.
 * Register    : intimate, quietly powerful, a little cinematic — warm memory
 *               emerging from cold cipher; not fearful, not clinical, not icy.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard named is a REAL Private Photo Vault feature (encrypted Cloud
 * Vault · only-you lock with Face ID / Touch ID / PIN · Decoy Vault · break-in
 * snapshots · app disguise · recently-deleted recovery). Static is an honest
 * visual metaphor for encryption — the copy says plainly that your photos are
 * locked in your vault and encrypted, so they are unreadable to anyone without
 * your key; it does not claim the phone's own Photos app shows literal noise.
 * No countdown, no fabricated scarcity, no data-loss threat (this is onboarding
 * — nothing to lose). "Save 79%" is real arithmetic from live product data.
 * Yearly is the featured, default, most prominent price; monthly is small &
 * secondary. The 7-day free trial is the real trial; billing terms read from
 * real products. Renders on built-in debug data with bundled photos — no native
 * injection, no external egress.
 */

// The real Private Photo Vault safeguards — one per tooth on the key. As the key
// seats, each tooth lights; together they are what actually turns the static
// back into a memory.
const TEETH = [
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private, encrypted backup — readable only with your key",
  },
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID, Touch ID, or a code only you know",
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Vault",
    line: "a second code opens a harmless, empty vault",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "a silent photo of anyone who tries a wrong code",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "hide the whole vault behind an ordinary-looking app",
  },
]

// The memory wall. Bundled, self-contained assets (reused from the repo). Sizes
// are grid spans so the mosaic reads as a real gallery, not a uniform grid.
const TILES = [
  { src: "/static/m1.jpg", col: "span 4", row: "span 2", d: 0.06 },
  { src: "/static/m7.jpg", col: "span 2", row: "span 1", d: 0.2 },
  { src: "/static/m3.jpg", col: "span 2", row: "span 1", d: 0.34 },
  { src: "/static/m2.jpg", col: "span 3", row: "span 1", d: 0.14 },
  { src: "/static/m4.jpg", col: "span 3", row: "span 1", d: 0.44 },
  { src: "/static/m5.jpg", col: "span 2", row: "span 1", d: 0.6 },
  { src: "/static/m8.jpg", col: "span 2", row: "span 1", d: 0.72 },
  { src: "/static/m6.jpg", col: "span 2", row: "span 1", d: 0.86 },
]

const RESOLVE_MS = 1250

const StaticPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── decrypt progress: 0 = full cipher-static, 1 = every memory resolved ────
  const [progress, setProgress] = useState(0)
  const progressRef = useRef(0)
  const setProg = useCallback((v: number) => {
    const c = Math.max(0, Math.min(1, v))
    progressRef.current = c
    setProgress(c)
  }, [])

  const userActed = useRef(false)
  const raf = useRef<number | null>(null)
  const stopRaf = useCallback(() => {
    if (raf.current != null) cancelAnimationFrame(raf.current)
    raf.current = null
  }, [])

  const animateTo = useCallback(
    (target: number, durationMs: number) => {
      stopRaf()
      const from = progressRef.current
      const start = performance.now()
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / durationMs)
        const eased = t * t * (3 - 2 * t) // smootherstep
        setProg(from + (target - from) * eased)
        if (t < 1) raf.current = requestAnimationFrame(step)
        else raf.current = null
      }
      raf.current = requestAnimationFrame(step)
    },
    [stopRaf, setProg],
  )

  // auto-demo on mount: sweep the key so the payoff reads in the first glance
  useEffect(() => {
    if (reduceMotion) {
      setProg(1)
      return
    }
    const t = window.setTimeout(() => {
      if (!userActed.current) animateTo(1, RESOLVE_MS)
    }, 480)
    return () => {
      window.clearTimeout(t)
      stopRaf()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // ── the key track (drag to decrypt) ───────────────────────────────────────
  const trackRef = useRef<HTMLDivElement | null>(null)
  const dragging = useRef(false)

  const setFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const pad = Math.min(28, r.width * 0.12) // key half-width inset
      const raw = (clientX - r.left - pad) / (r.width - pad * 2)
      setProg(raw)
    },
    [setProg],
  )

  const onDown = useCallback(
    (e: React.PointerEvent) => {
      userActed.current = true
      stopRaf()
      dragging.current = true
      e.currentTarget.setPointerCapture?.(e.pointerId)
      setFromClientX(e.clientX)
    },
    [stopRaf, setFromClientX],
  )
  const onMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return
      setFromClientX(e.clientX)
    },
    [setFromClientX],
  )
  const onUp = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    // snap decisively: past the halfway point it seats home; short of it, the
    // key falls back and the wall re-scrambles.
    if (progressRef.current >= 0.5) animateTo(1, 420)
    else animateTo(0, 520)
  }, [animateTo])

  const onKey = useCallback(
    (e: React.KeyboardEvent) => {
      userActed.current = true
      if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        e.preventDefault()
        stopRaf()
        setProg(progressRef.current + 0.12)
      } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        e.preventDefault()
        stopRaf()
        setProg(progressRef.current - 0.12)
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        animateTo(progressRef.current >= 0.999 ? 0 : 1, RESOLVE_MS)
      }
    },
    [stopRaf, setProg, animateTo],
  )

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const p = progress
  const resolved = p >= 0.999
  // teeth seat progressively as the key travels the back half of the stroke
  const seatedTeeth = useMemo(
    () => TEETH.map((_, i) => p >= 0.42 + (i / TEETH.length) * 0.5),
    [p],
  )
  const seatedCount = seatedTeeth.filter(Boolean).length
  const keyPct = Math.round(p * 100)

  // displacement scale for the shared decrypt filter (grain melts as p→1)
  const dispScale = Math.round(46 * Math.pow(1 - p, 1.35))

  return (
    <div
      className={`${styles.root} ${resolved ? styles.isResolved : ""}`}
      style={{ ["--p" as any]: p }}
    >
      {/* the encryption filter every tile shares — turbulence displacement +
          a fractal-noise "static" plate whose opacity is driven by (1 - p) */}
      <svg className={styles.defs} aria-hidden="true">
        <defs>
          <filter id="staticDecrypt" x="-8%" y="-8%" width="116%" height="116%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.019 0.024"
              numOctaves={2}
              seed={7}
              stitchTiles="stitch"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale={dispScale}
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <section className={styles.intro}>
        <span className={styles.kicker}>
          <span className={styles.kickerDot} aria-hidden="true" />
          Encrypted the moment it&rsquo;s yours
        </span>
        <h1 className={styles.title}>
          To everyone else, <span className={styles.noiseWord}>static.</span>
          <br />
          To you, <span className={styles.titleAccent}>everything.</span>
        </h1>
      </section>

      {/* ── the encryption wall ──────────────────────────────────────────── */}
      <section className={styles.stage}>
        <div className={styles.wall} aria-hidden="true">
          {TILES.map((t, i) => {
            // each tile resolves slightly out of phase for a "grain by grain"
            // sweep across the wall (left/top resolve first)
            const local = Math.max(0, Math.min(1, (p - t.d) / (1 - t.d)))
            return (
              <div
                key={i}
                className={styles.tile}
                style={{
                  gridColumn: t.col,
                  gridRow: t.row,
                  ["--local" as any]: local,
                }}
              >
                <div
                  className={styles.tilePhoto}
                  style={{
                    backgroundImage: `url(${t.src})`,
                    filter: p < 0.985 ? "url(#staticDecrypt)" : undefined,
                  }}
                />
                <div className={styles.tileStatic} />
                <div className={styles.tileTint} />
              </div>
            )
          })}
          <div className={styles.wallGrain} />
        </div>

        <div className={styles.readout} aria-live="polite">
          <span className={styles.readState}>
            {resolved ? "Decrypted — only on your key" : "Cipher-static"}
          </span>
          <span className={styles.readCount}>
            <b>{keyPct}</b>
            <span>%</span>
          </span>
        </div>
      </section>

      {/* ── SLIDE-YOUR-KEY: the interaction + the real safeguards as teeth ── */}
      <div className={styles.keyBlock}>
        <div
          ref={trackRef}
          className={styles.track}
          role="slider"
          tabIndex={0}
          aria-label="Slide your key to decrypt your memories"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={keyPct}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onKeyDown={onKey}
        >
          <div className={styles.trackFill} style={{ width: `${p * 100}%` }} />
          <span className={styles.trackHint}>
            {resolved ? "Your memories, back" : "Slide your key"}
          </span>
          <div
            className={styles.key}
            style={{ left: `${p * 100}%` }}
            aria-hidden="true"
          >
            <span className={styles.keyBow} />
            <span className={styles.keyBlade} />
            <span className={styles.keyTeeth}>
              {TEETH.map((_, i) => (
                <span
                  key={i}
                  className={`${styles.keyTooth} ${
                    seatedTeeth[i] ? styles.keyToothOn : ""
                  }`}
                />
              ))}
            </span>
          </div>
        </div>

        <ul className={styles.teeth}>
          {TEETH.map((tooth, i) => {
            const on = seatedTeeth[i] || resolved
            return (
              <li
                key={tooth.key}
                className={`${styles.tooth} ${on ? styles.toothOn : ""}`}
              >
                <span className={styles.toothIcon} aria-hidden="true">
                  <tooth.Icon width={16} height={16} />
                </span>
                <span className={styles.toothText}>
                  <span className={styles.toothName}>{tooth.name}</span>
                  <span className={styles.toothLine}>{tooth.line}</span>
                </span>
                <span className={styles.toothDot} aria-hidden="true" />
              </li>
            )
          })}
        </ul>
      </div>

      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        Locked in your vault and encrypted in the cloud, your photos are
        unreadable to anyone without your key.{" "}
        {seatedCount === TEETH.length
          ? "All five safeguards, on from day one."
          : "Five real safeguards turn the static back into your memories."}
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ──────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${
            plan === "yearly" ? styles.selected : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Yearly · your own key</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            7 days free, then about {yearlyPerMonth}/mo billed yearly
          </span>
        </button>

        <button
          className={`${styles.monthly} ${
            plan === "monthly" ? styles.monthlySel : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Get my key — start free"
              : "Get my key — monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default StaticPaywall
